import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    influencersFromConnectedInstagramUrl,
    influencersGuestOAuthLinkUrl,
    influencersOAuthConnectUrl,
    influencersUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import {
    clearInfluencerPrelinkSession,
    loadInfluencerPrelinkFromSession,
    parseInfluencerPrelinkResponse,
    type GuestOAuthLinkPayload,
    type InfluencerPrelinkResponse,
} from '../../utils/influencerPrelink';
import {
    claimPendingInfluencer,
    claimPendingInfluencerFail,
    claimPendingInfluencerSuccess,
    postInfluencerFromConnectedInstagram,
    postInfluencerFromConnectedInstagramFail,
    postInfluencerFromConnectedInstagramSuccess,
    postInfluencerGuestOAuthLink,
    postInfluencerGuestOAuthLinkFail,
    postInfluencerGuestOAuthLinkSuccess,
    postInfluencerOAuthConnect,
    postInfluencerOAuthConnectFail,
    postInfluencerOAuthConnectSuccess,
} from './actions';

function settle(
    resolve: ((v: any) => void) | undefined,
    reject: ((e: any) => void) | undefined,
    value: any,
    isError = false,
) {
    if (isError) {
        if (typeof reject === 'function') reject(value);
        return;
    }
    if (typeof resolve === 'function') resolve(value);
}

function* guestOAuthLinkSaga({
    payload,
    resolve,
    reject,
}: {
    payload: GuestOAuthLinkPayload;
    resolve?: (v: InfluencerPrelinkResponse) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, influencersGuestOAuthLinkUrl, httpRequest.POST, payload);
        const data = parseInfluencerPrelinkResponse(res);
        yield put(postInfluencerGuestOAuthLinkSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(postInfluencerGuestOAuthLinkFail(error));
        settle(resolve, reject, error, true);
    }
}

function* oauthConnectSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { code: string; provider: string };
    resolve?: (v: void) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        yield call(api, influencersOAuthConnectUrl, httpRequest.POST, payload);
        yield put(postInfluencerOAuthConnectSuccess());
        settle(resolve, reject, undefined);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(postInfluencerOAuthConnectFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fromConnectedInstagramSaga({
    resolve,
    reject,
}: {
    resolve?: (v: void) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        yield call(api, influencersFromConnectedInstagramUrl, httpRequest.POST, {});
        yield put(postInfluencerFromConnectedInstagramSuccess());
        settle(resolve, reject, undefined);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(postInfluencerFromConnectedInstagramFail(error));
        settle(resolve, reject, error, true);
    }
}

function* claimPendingSaga({
    resolve,
    reject,
}: {
    resolve?: (v: boolean) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    const pending = loadInfluencerPrelinkFromSession();
    if (!pending) {
        yield put(claimPendingInfluencerSuccess(false));
        settle(resolve, reject, false);
        return;
    }
    try {
        yield call(api, `${influencersUrl}/${pending.influencer_id}/claim`, httpRequest.PATCH, {
            claim_token: pending.claim_token,
        });
        clearInfluencerPrelinkSession();
        yield put(claimPendingInfluencerSuccess(true));
        settle(resolve, reject, true);
    } catch (error: any) {
        clearInfluencerPrelinkSession();
        yield put(claimPendingInfluencerFail(error));
        // Original util resolved false on failure (token consumed / invalid) — keep that contract.
        settle(resolve, reject, false);
    }
}

export default function* influencerPrelinkSaga() {
    yield all([
        takeLatest(postInfluencerGuestOAuthLink as any, guestOAuthLinkSaga),
        takeLatest(postInfluencerOAuthConnect as any, oauthConnectSaga),
        takeLatest(postInfluencerFromConnectedInstagram as any, fromConnectedInstagramSaga),
        takeLatest(claimPendingInfluencer as any, claimPendingSaga),
    ]);
}
